import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Stethoscope, HeartHandshake, IndianRupee } from "lucide-react";
import { THEME, servicesOf, doctorFor, STEPS, CONTACT } from "@/data/site";
import { Reveal, SectionHead } from "@/components/common/Reveal";

const TICKER = [
  "नि:शुल्क नेत्र जाँच — हर बृहस्पतिवार", "बिना टाँका फेको मोतियाबिंद ऑपरेशन", "दूरबीन से पित्त की थैली का ऑपरेशन",
  "ग्लूकोमा व रेटिना जाँच", "हार्निया व हाइड्रोसील सर्जरी", "बवासीर, फिशर व भगंदर का इलाज", "ऑप्टिकल व फ़ार्मेसी",
];

export const Ticker = () => (
  <div data-testid="services-ticker" className="overflow-hidden border-y border-line bg-white py-4">
    <div className="marquee gap-10">
      {[...TICKER, ...TICKER].map((t, i) => (
        <span key={i} className="flex items-center gap-3 whitespace-nowrap text-[15px] font-semibold text-ink/80">
          <span className={`h-2 w-2 rounded-full ${i % 2 ? "bg-surg" : "bg-maroon"}`} /> {t}
        </span>
      ))}
    </div>
  </div>
);

const DeptCard = ({ dept, delay }) => {
  const t = THEME[dept];
  const d = doctorFor(dept);
  const list = servicesOf(dept);
  return (
    <Reveal delay={delay}>
      <article data-testid={`dept-card-${dept}`} className="card-lift group relative h-full overflow-hidden rounded-[32px] bg-white">
        <div className="relative h-64 overflow-hidden md:h-72">
          <img src={t.image} alt={t.deptEn} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-5 left-6 right-6 text-white">
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${t.bg}`}>{list.length} सेवाएँ</span>
            <h3 className="font-display mt-3 text-3xl">{t.deptHi}</h3>
            <p className="font-en text-sm text-white/80">{t.deptEn} · {d.nameEn}</p>
          </div>
        </div>
        <div className="p-6 md:p-8">
          <ul className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {list.slice(0, 6).map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className={`flex items-center gap-2 text-[15px] text-ink transition-colors hover:${t.text}`}>
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${t.bg}`} /> {s.titleHi}
                </Link>
              </li>
            ))}
          </ul>
          <Link to={t.path} data-testid={`dept-card-link-${dept}`} className={`btn ${t.btn} mt-7`}>{t.labelHi} — सभी सेवाएँ <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </article>
    </Reveal>
  );
};

export const DeptSplit = () => (
  <section data-testid="dept-split-section" className="py-20 md:py-28">
    <div className="container-x">
      <SectionHead eyebrow="दो विभाग · Two Departments" title="एक छत के नीचे — आँख और सर्जरी की संपूर्ण देखभाल" sub="Choose your department to explore treatments, or book directly with the right specialist." />
      <div className="mt-12 grid gap-8 lg:grid-cols-2">
        <DeptCard dept="eye" delay={0} />
        <DeptCard dept="surgery" delay={0.12} />
      </div>
    </div>
  </section>
);

const WHY = [
  { icon: Stethoscope, hi: "अनुभवी विशेषज्ञ", en: "LVPEI-trained eye surgeon & DNB laparoscopic surgeon" },
  { icon: BadgeCheck, hi: "आधुनिक तकनीक", en: "Computerized eye diagnostics & keyhole surgery" },
  { icon: HeartHandshake, hi: "सरल भाषा में परामर्श", en: "Every step explained clearly to you and family" },
  { icon: IndianRupee, hi: "किफ़ायती इलाज", en: "Honest pricing, no unnecessary tests" },
];

export const Process = () => (
  <section data-testid="process-section" className="grain relative overflow-hidden bg-maroon-ink py-20 md:py-28">
    <div className="container-x relative grid gap-14 lg:grid-cols-2">
      <div>
        <SectionHead light eyebrow="आपकी यात्रा · Your Visit" title="चार आसान कदम, साफ़ रास्ता" sub="From booking to recovery — we walk with you." />
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {WHY.map((w, i) => (
            <Reveal key={w.hi} delay={i * 0.06}>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <w.icon className="h-6 w-6 text-amber-300" />
                <p className="mt-3 font-semibold text-white">{w.hi}</p>
                <p className="font-en mt-1 text-xs text-white/60">{w.en}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <ol className="relative space-y-5 border-l border-white/15 pl-8">
        {STEPS.map((s, i) => (
          <Reveal key={s.en} delay={i * 0.1}>
            <li data-testid={`process-step-${i + 1}`} className="relative rounded-2xl bg-white p-6">
              <span className={`font-en absolute -left-[52px] top-6 flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white ${i % 2 ? "bg-surg" : "bg-maroon"}`}>0{i + 1}</span>
              <p className="text-lg font-semibold text-ink">{s.hi}</p>
              <p className="font-en text-sm text-muted2">{s.en}</p>
            </li>
          </Reveal>
        ))}
        <Reveal delay={0.4}>
          <p className="rounded-2xl bg-amber-400/15 p-5 text-amber-200"><BadgeCheck className="mr-2 inline h-5 w-5" />{CONTACT.freeCheckupHi}</p>
        </Reveal>
      </ol>
    </div>
  </section>
);
